import { Column } from '@blueprintjs/table';
import { Button, Divider, Input, List } from 'antd';
import BlueprintTableInfiniteScroller from 'components/BlueprintTableInfiniteScroller';
import WithOrgData, { WithOrgDataProps } from 'components/WithOrgData';
import gql from 'graphql-tag';
import React, { useRef } from 'react';
import { DuplicationChecker, MDDetails, MDHeader, MDLayout, MDTable, useMasterDataState } from '..';
import AircraftEntryForm from '../entryForms/aircraft';
import TableLoader from '../TableLoader';
import { getMDTableProps, MasterDataProps, MDStateToDetailsProps, MDStateToHeaderProps, MDStateToLayoutProps, MDStateToTableScrollerProps, renderCellFromKeyIndex, renderNoData, searchValuesToQuerySearchPayload, trimString } from '../util';
import DeleteEntity from 'Mutations/DeleteEntity';



export interface MDAircraftProps extends WithOrgDataProps, MasterDataProps {

}

const QUERY = gql`
    query AircraftQuery($filter: [FilterByKeyValueInput!], $search: [FilterByKeyValueInput!], $limit: Int, $skip: Int) {
        MasterDataAircrafts(filter: $filter, search: $search, limit: $limit, skip: $skip) {
            docs {
                ... on Aircraft {
                    _id
                    _rev
                    tailNum
                    model
                    maxSeats
                    maxLoad
                    maxCargoLoad
                }
            }
        }
    }
`

const MUTATION = gql`
    mutation AircraftMutation(
        $_id: ID
        $tailNum: String!
        $model: String!
        $maxSeats: Int!
        $maxLoad: Int!
        $maxCargoLoad: Int,
        $tpID: ID!
    ){
        setAircraft(
            _id: $_id
            tailNum: $tailNum
            model: $model
            maxSeats: $maxSeats
            maxLoad: $maxLoad
            maxCargoLoad: $maxCargoLoad
            tpID: $tpID
        ){
            _id
            _rev
            tailNum
            model
            maxSeats
            maxLoad
            maxCargoLoad
            tpID {
                _id
                name
            }
        }
    }
`

const MDAircraft: React.FC<MDAircraftProps> = (props) => {

    const formRef = useRef(null);

    let tpID = props.orgData.getOrgIDByType('transporter');

    const MDState = useMasterDataState({
        getQueryData: (data: any) => data.MasterDataAircrafts.docs,
        getQueryVariables: (searchValues) => ({
            filter: [
                {
                    key: 'tpID',
                    value: JSON.stringify(tpID)
                }
            ],
            search: searchValuesToQuerySearchPayload(searchValues)
        }),
        queryGQL: QUERY,
        saveMutationGQL: MUTATION,
        deleteMutationGQL: DeleteEntity,
        tpID: tpID,
        paginationLimit: props.dataPaginationLimit,
        pollInterval: props.pollInterval,
        updateQueryAfterFetchMore: (prev, fetchMoreResult) => {
            return {
                ...prev,
                MasterDataAircrafts: {
                    ...prev.MasterDataAircrafts,
                    docs: [
                        ...prev.MasterDataAircrafts.docs,
                        ...fetchMoreResult.MasterDataAircrafts.docs
                    ]
                }
            }
        }
    })

    // const [ save, { loading: saving, error: saveError, data: savedData } ] = useMutation(MUTATION, {
    //     onCompleted: () => {
    //         MDState.cancelEditing(false);
    //     }
    // });

    const data = MDState.data;

    return <MDLayout
        {...MDStateToLayoutProps(MDState)}
        noDataElement={renderNoData(MDState, () => {
            return MDState.editFromNew({
                tailNum: MDState.getSearchValue('tailNum'),
                model: MDState.getSearchValue('model')
            })
        })}
        headerElement={<MDHeader
            {...MDStateToHeaderProps(MDState)}
            inputElement={
                <Input.Group compact>
                    <Input
                        onChange={(e) => MDState.onSearchValueChange('tailNum', e.target.value)} placeholder="Search by Tail Number"
                        value={MDState.getSearchValue('tailNum')}
                        style={{ width:'15rem' }}
                        allowClear
                    />
                    <Input
                        onChange={(e) => MDState.onSearchValueChange('model', e.target.value)} placeholder="By Model"
                        value={MDState.getSearchValue('model')}
                        style={{ width:'15rem' }}
                        allowClear
                    />
                </Input.Group>
            }
        />}
        tableElement={<TableLoader mdState={MDState}>
                <BlueprintTableInfiniteScroller {...MDStateToTableScrollerProps(MDState)}>
                    <MDTable
                        {...getMDTableProps(data, MDState)}
                        hideDelete
                    >
                        <Column name="Tail Num" cellRenderer={renderCellFromKeyIndex(data, 'tailNum')} />
                        <Column name="Model" cellRenderer={renderCellFromKeyIndex(data, 'model')} />
                        <Column name="Max Seats" cellRenderer={renderCellFromKeyIndex(data, 'maxSeats')} />
                        <Column name="Max Load" cellRenderer={renderCellFromKeyIndex(data, 'maxLoad')} />
                        <Column name="Max Cargo Load" cellRenderer={renderCellFromKeyIndex(data, 'maxCargoLoad')} />
                    </MDTable>
                </BlueprintTableInfiniteScroller>
            </TableLoader>}
        detailsElement={<MDDetails
            {...MDStateToDetailsProps(MDState, 'tailNum')}
            onSave={() => {
                formRef.current.validateFieldsAndScroll((err, values) => {
                    if (!err){
                        MDState.save({
                            ...values,
                            tailNum: trimString(values.tailNum),
                            model: trimString(values.model),
                            maxSeats: values.maxSeats || 0,
                            maxLoad: values.maxLoad || 0,
                            _id: MDState.getEntryFieldValue('_id')
                        })
                    }
                })
            }}
        >
            <AircraftEntryForm
                formFields={MDState.entryFields}
                onFieldsChange={(_, fields) => MDState.setEntryFields(fields)}
                uppercaseFields={['tailNum', 'model']}
                autoFocus
                ref={formRef}
                dupeChecker={MDState.isNewEntry ? (
                    <DuplicationChecker
                        skip={!trimString(MDState.getEntryFieldValue('tailNum'))}
                        getData={data => data && data.MasterDataAircrafts && data.MasterDataAircrafts.docs}
                        renderItems={(items) => {
                            return <List size="small">
                                {items.map((craft) =>
                                    <List.Item>
                                        <Button
                                            className="mc-link-btn"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                MDState.editFromExisting(craft, false)
                                            }}
                                    >
                                        <strong>Tail Num: </strong>{craft.tailNum}
                                        <Divider type="vertical" />
                                        <strong>Model: </strong>{craft.model}
                                    </Button>
                                    </List.Item>
                                )}
                            </List>
                        }}
                        query={QUERY}
                        variables={{
                            filter: [
                                {
                                    key: 'tpID',
                                    value: JSON.stringify(props.orgData.getOrgIDByType('transporter'))
                                }
                            ],
                            search: [
                                {
                                    key: 'tailNum',
                                    value: JSON.stringify(trimString(MDState.getEntryFieldValue('tailNum')))
                                }
                            ]
                        }}
                    />
                ) : null
                }
            />
            <label style={{fontSize:'5px'}}>{MDState.getEntryFieldValue('_id')}</label>
        </MDDetails>}
    />
}

export default WithOrgData(MDAircraft)